import _debounce from 'lodash/debounce';
import { Form } from 'react-final-form';
import { WeddingSearchHit } from '@zola/svc-web-api-ts-client';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { InputFieldAddOnType } from '@zola/zola-ui/src/components/Form/InputFieldV2/InputFieldAddOn';
import InputFieldV3 from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import ApiService from 'libs/server/apiService';
import { parse } from 'querystring';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Accent } from 'components/TextBlock/TextBlock.styles';
import { Container, InputWrapper, StyledH1, StyledH2 } from './CoupleSearch.styles';

// update query param without redirect by using History API
const updateQueryParam = (searchQuery: string, result?: Array<WeddingSearchHit>): void => {
  const url = new URL(window.location.href);
  url.searchParams.set('query', searchQuery);
  window.history.pushState({ query: searchQuery, result }, '', url.toString());
};

/**
 * helper function for handling search result.
 * @param searchQuery
 */
const fetchCoupleResult = async (
  searchQuery: string,
  handleSearchResult: Dispatch<SetStateAction<Array<WeddingSearchHit> | undefined>>
): Promise<void> => {
  if (searchQuery) {
    const URL = `${window.location.origin}/website-nav/web-api/v1/search/couples`;
    const result = await ApiService.post<WeddingSearchHit[]>(URL, {
      query: searchQuery,
    });
    // update query param
    updateQueryParam(searchQuery, result);
    // update search result for the parent
    handleSearchResult(result);
  } else {
    // clear search result when there is no search query
    handleSearchResult(undefined);
  }
};

// Handle Search button click event
const handleButtonClick = (
  query: string | undefined,
  handleSearchResult: Dispatch<SetStateAction<Array<WeddingSearchHit> | undefined>>,
  searchResultsRef?: React.RefObject<HTMLDivElement>
): void => {
  if (query) {
    fetchCoupleResult(query, handleSearchResult)
      .then(() => {
        const searchResultsDiv = searchResultsRef?.current;
        if (searchResultsDiv) {
          const navHeight = document.getElementById('navContainerSSG')?.clientHeight || 80;
          const top = searchResultsDiv.getBoundingClientRect().top + window.scrollY - navHeight;
          window.scrollTo({
            behavior: 'smooth',
            top,
          });
        }
      })
      .catch(() => undefined);
  }
};

const searchOnKeyPress = _debounce(handleButtonClick, 300);

type CoupleSearchProps = {
  handleSearchResult: Dispatch<SetStateAction<Array<WeddingSearchHit> | undefined>>;
  searchResultsRef?: React.RefObject<HTMLDivElement>;
};

const CoupleSearch: React.FC<CoupleSearchProps> = props => {
  const { handleSearchResult, searchResultsRef } = props;

  const [query, setQuery] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleClear = () => {
    // clear query from state, queryParam, and clear out results
    setQuery('');
    updateQueryParam('');
    handleSearchResult(undefined);
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  };

  // Handle Browser back button
  useEffect(() => {
    window.onpopstate = () => {
      const queryString = window.history.state.query;
      const { result } = window.history.state;
      // update query which is used by input
      setQuery(queryString);
      // pass up search results without updating browser history
      handleSearchResult(result);
    };

    return () => {
      window.onpopstate = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger search when search query is provided by query param
  useEffect(() => {
    const { query: name } = parse(window.location.search.split('?')[1]);
    if (typeof name === 'string') {
      setQuery(name);
      fetchCoupleResult(name, handleSearchResult).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // theoretically this should be accomplished with the autoFocus attribute directly on the input, however that is not working

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  return (
    <Form
      onSubmit={() => handleButtonClick(query, handleSearchResult, searchResultsRef)}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Container>
            <StyledH1 presentation="h3">
              Find a couple&apos;s <Accent>wedding registry</Accent> or <Accent>website</Accent>
            </StyledH1>
            <StyledH2 presentation="h5">
              Looking for a{' '}
              <LinkV2 subtle noTextTransform href="https://baby.zola.com/search/baby-registry">
                baby registry
              </LinkV2>
              ?
            </StyledH2>
            <InputWrapper>
              <InputFieldV3
                ref={inputRef}
                clear
                id="couple-search-input"
                inline
                isControlled
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  setQuery(e.target.value);
                  searchOnKeyPress(e.target.value, handleSearchResult);
                }}
                onClear={handleClear}
                placeholder="Search by first and last name"
                type="text"
                value={query}
                addOn={InputFieldAddOnType.COUPLE_SEARCH}
                /* @ts-expect-error: Supported in react 17 https://github.com/necolas/react-native-web/pull/1707 */
                enterKeyHint="search"
              />
              <ButtonV3 size="large" disabled={!query} onClick={handleSubmit}>
                Search
              </ButtonV3>
            </InputWrapper>
          </Container>
        </form>
      )}
    />
  );
};

export default CoupleSearch;
